import { RequestErrorResponse, NetworkRequestError, ResponseField } from './'
import { formatError } from '../../redux/lib/errors'

export const getServiceHeaders = (
  token: string,
  addl: HeadersInit | undefined = undefined
): HeadersInit => {
  const headers: HeadersInit =
    addl?.keys && 'Content-Type' in addl
      ? addl
      : {
          ...addl,
          'Content-Type': 'application/json'
        }
  if (typeof token === 'string') {
    return {
      ...headers,
      Authorization: `Bearer ${token.replace(/['"]+/g, '')}`
    }
  } else {
    return headers
  }
}

export const getAccessToken = (): string => {
  //better way?
  return localStorage.getItem('wivi.access_token') || ''
}

export const getServiceResponse = async <T>(
  response: Response,
  text?: boolean
): Promise<T> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any = null
  if (response.headers.get('Authorization') == 'Password is expired') {
    throw new NetworkRequestError('auth', [], 'Password is expired')
  }
  if (!response.ok) {
    //will always throw
    const e = await getServiceResponseError(response)
    throw e
  }
  if (response.status !== 204) {
    try {
      if (text && response.ok) {
        data = await response.text()
      } else {
        data = await response.json()
      }
    } catch (e) {
      data = null
    }
  }

  return data
}

export const getServiceResponseError = async (response: Response) => {
  let data: null | {
    type?: string
    message?: string
    fields?: []
    error?: ResponseField
  } = null
  try {
    data = await response.json()
    //will be json or nothing response.json()
  } catch (e) {
    data = null
  }

  const error = {
    code: (data && data?.type) || 'error',
    message:
      (data && data.message) ||
      response.statusText ||
      'Unknown Error x03391aa00',
    fields: (data && data.fields) || []
  }

  switch (response.status) {
    case 400:
      return new NetworkRequestError('error', [], 'Bad Request')

    case 401:
      return new NetworkRequestError(
        'auth',
        data?.error ? [data.error] : [],
        data?.message || ''
      )

    case 403:
      return new NetworkRequestError('error', [], 'Forbidden')
    case 404:
      return new NetworkRequestError('error', [], 'Resource not found')
    case 502:
      return new NetworkRequestError('error', [], response.statusText)
    case 500:
      return new NetworkRequestError('error', [], 'Internal Server Error')
  }
  return error
}

export const getServiceUrl = (
  path: string,
  options: { [k: string]: string | number } = {}
): string => {
  if (typeof path !== 'string')
    throw Object.assign(new Error('path must be of type string'), {
      code: 'invalid',
      fields: []
    })

  if (path.charAt(0) !== '/')
    throw Object.assign(
      new Error('path must begin with a / and not include the base url'),
      {
        code: 'invalid',
        fields: []
      }
    )

  if (typeof options !== 'object')
    throw Object.assign(new Error('options must be of type object'), {
      code: 'invalid',
      fields: []
    })

  const query = Object.entries(options)
    .map(([key, value]) => {
      const encodedKey = encodeURIComponent(key)
      const encodedValue = encodeURIComponent(String(value))
      return `${encodedKey}=${encodedValue}`
    })
    .join('&')

  return `${path}${query.length ? '?' + query : ''}`
}

export const handleClientError = (
  err: RequestErrorResponse
): NetworkRequestError | undefined => {
  if (err != undefined) {
    return formatError(err)
  }
}

export const fetchWithTimeout = async (
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: RequestInit | undefined | any
): Promise<Response | undefined> => {
  try {
    const { timeout = 5000 } = options

    const controller = new AbortController()
    const id = setTimeout(() => controller.abort(), timeout)

    const response = await fetch(path, {
      ...options,
      signal: controller.signal
    })
    clearTimeout(id)

    return response
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    handleClientError(err)
  }
}
